// EventDetail.jsx
import React, { useState }  from 'react';

import { useTranslation } from 'react-i18next';
import DeleteRecurrenceModal from './DeleteRecurrenceModal';


const EventDetail = ({ event, onEdit, userId, onDelete, onClose, onOpenWhiteboard, prompt, key, highlightEventId }) => {
  const { i18n, t } = useTranslation();
  const formatDate = (dateStr, allDay) => {
    if (!dateStr) return "";
    return new Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      ...(allDay ? {} : { hour: '2-digit', minute: '2-digit', hour12: false }),
    }).format(new Date(dateStr));
  };

const [showDeleteModal, setShowDeleteModal] = useState(false);

const iconBlueStyle = {
  fontSize: '22px',
  color: '#007bff',
};

const iconRedStyle = {
  fontSize: '22px',
  color: 'red',
};

const eventColor =
  event.visibility === 'private'
    ? '#888'
    : (event.color || '#3788d8');

const recurrenceSummary = () => {
  const rec = event.recurrence;
  if (!rec?.freq || rec.freq === 'none') return null;

  const freqLabel = {
    daily:   t('agenda.days'),
    weekly:  t('agenda.weeks'),
    monthly: t('agenda.months'),
  }[rec.freq];

  let summary = `🔁 ${t('agenda.every')} ${rec.interval || 1} ${freqLabel}`;

  if (rec.freq === 'weekly' && rec.daysOfWeek?.length > 0) {
    const dayNames = {
      fr: ['dim.','lun.','mar.','mer.','jeu.','ven.','sam.'],
      en: ['sun.','mon.','tue.','wed.','thu.','fri.','sat.'],
    };
    const names = dayNames[i18n.language] || dayNames.fr;
    summary += ` (${rec.daysOfWeek.map(d => names[d]).join(', ')})`;
  }

  if (rec.endDate) {
    summary += ` — ${t('agenda.until')} ${new Date(rec.endDate).toLocaleDateString(i18n.language)}`;
  }

  return summary;
};
 
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--bg-color, #fff)', borderRadius: 12,
        padding: 24, width: '100%', maxWidth: 480,
        display: 'flex', flexDirection: 'column', gap: 16,
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Pastille couleur */}
            <div style={{
              width: 14, height: 14, borderRadius: '50%',
              backgroundColor: eventColor, flexShrink: 0,
            }} />
            <h3 style={{ margin: 0, fontSize: 17, wordBreak: 'break-word' }}>
              {event.visibility === 'private' && <span style={{ marginRight: 6 }}>🔒</span>}
              {event.title}
            </h3>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {/* Bouton édition et suppression */}
		{userId === event.createdBy && (
		<>
            <button onClick={onEdit} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
            }} title={t('agenda.edit')}>
              <i className="ti ti-edit" style={iconBlueStyle} aria-hidden="true" />
            </button>
		
            <button 
	onClick={() => {
      if (!recurrenceSummary()) {
        onDelete(event.id);
      } else {
        setShowDeleteModal(true);
      }
    }}
 

	style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
            }} title={t('agenda.sup')}>
              <i className="ti ti-trash" style={iconRedStyle} aria-hidden="true" />
            </button>
		</>
		)}
            
            {/* Bouton fermer */}
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'red',
            }}><i className="ti ti-circle-x" style={iconRedStyle} aria-hidden="true" /></button>
          </div>
        </div>

	{/* créateur */}
	<div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
	<span>👤</span>
	<span>{event.creatorName || event.createdBy}</span>
	</div>

        {/* Dates */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <span>🗓️</span>
            <span>{formatDate(event.start, event.allDay)}</span>
          </div>
          {event.end && event.end !== event.start && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#666' }}>
              <span>⏱️</span>
              <span>{formatDate(event.end, event.allDay)}</span>
            </div>
          )}
          {event.allDay && (
            <div style={{ fontSize: 12, color: '#888', marginLeft: 24 }}>
              {t('agenda.allday')}
            </div>
          )}
        </div>
{/* Récurrence */}
        {recurrenceSummary() && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 13, color: '#3788d8', background: '#e8f0fe',
            borderRadius: 8, padding: '8px 12px',
          }}>
            {recurrenceSummary()}
          </div>
        )}
        {/* Description */}
        {event.description && (
          <div style={{
            fontSize: 13, color: '#444',
            borderTop: '1px solid #eee', paddingTop: 12,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
		marginLeft: 24,
          }}>
            
            {event.description}
          </div>
        )}

        {/* Tableaux liés */}
        {event.whiteboards?.length > 0 && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
              ✍️ {t('agenda.tableaux')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {event.whiteboards.map((wb) => (
                <span
                  key={wb.whiteboardId}
                  onClick={() => onOpenWhiteboard(wb.whiteboardId)}
                  style={{
                    fontSize: 13, cursor: 'pointer',
                    color: '#3788d8', textDecoration: 'underline',
                    padding: '4px 8px', background: '#f0f4ff',
                    borderRadius: 6, display: 'inline-block',
                  }}
                >
                  ⚪ {wb.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Visibilité */}
        <div style={{ fontSize: 12, color: '#888', borderTop: '1px solid #eee', paddingTop: 8 }}>
          {event.visibility === 'private' ? `🔒 ${t('agenda.private')}` : `🌐 ${t('agenda.shared')}`}
        </div>


      </div>
{showDeleteModal && (
  <DeleteRecurrenceModal
    isRecurring={!!event?.recurrence && event.recurrence.freq !== 'none'}
    onChoice={(mode) => {
      setShowDeleteModal(false);
      onDelete(event.id, mode); // ← passer le mode au parent
    }}
    onClose={() => setShowDeleteModal(false)}
  />
)}

    </div>
  );
};

export default EventDetail;